import type { MeshPoint } from './coordsFromPk';

export interface MapGeometryInput {
  /** `pkInit === pkEnd`: la actuación ocurre en un sitio, no a lo largo de un trecho. */
  isPunctual: boolean;
  /** Coordenada exacta del PK resuelta contra la malla precacheada, si la hay. */
  meshPoint: MeshPoint | null;
  /** Vértices del trazado que devolvió el servidor (vacío si no se consultó). */
  vertices: MeshPoint[];
}

export interface MapGeometry {
  /** Dónde encuadra el mapa. */
  center: MeshPoint | null;
  /** Marcador de ubicación puntual. Excluyente con `path`. */
  point: MeshPoint | null;
  /** Polilínea del trecho. Excluyente con `point`. */
  path: MeshPoint[];
}

/**
 * Qué se pinta en el mapa de una ubicación: un PUNTO o un TRECHO.
 *
 * ── Por qué existe ─────────────────────────────────────────────────────────
 *
 * Una actuación puntual (`pkInit === pkEnd`) se dibujaba como una línea gruesa
 * de más de medio kilómetro. No era un fallo del dibujo: `drawingWindow`
 * ensancha ±300 m para que el servidor devuelva vértices con los que trazar
 * algo, y esa ventana de DIBUJO acababa pintada tal cual. La actuación está en
 * un sitio, así que lo que hay que pintar es un punto.
 *
 * En el resumen no interviene esa ventana y pasaba lo mismo por otro camino:
 * medido contra beta, `geoPositionRoads` con `pkInit === pkEnd` (187+151 de la
 * C-25) devuelve **11 vértices**, no cero — el servidor sirve el entorno del PK
 * —, así que también salía una línea.
 *
 * La decisión vive aquí, y no en cada pantalla, porque la toman DOS sitios —el
 * asistente de Actuación y el selector del resumen— y tienen que decir lo
 * mismo: la misma actuación no puede verse de dos formas según por dónde se
 * mire.
 *
 * ── Orden de preferencia para el punto ─────────────────────────────────────
 *
 * 1. La malla precacheada, que da la coordenada EXACTA del PK y además
 *    funciona sin cobertura.
 * 2. El centro de la ventana dibujada, que es lo más cerca del PK que se puede
 *    estar cuando no hay malla.
 * 3. Nada. Un punto inventado es peor que ninguno.
 *
 * En los tres casos `path` va vacío: si es un punto, **nunca** se dibuja línea.
 */
export function locationMapGeometry({
  isPunctual,
  meshPoint,
  vertices,
}: MapGeometryInput): MapGeometry {
  const center = centerOf(vertices);

  if (isPunctual) {
    const point = meshPoint ?? center;
    return { center: point, point, path: [] };
  }

  // Un trecho del que solo se conoce UN vértice no es una línea: una
  // polilínea de un punto no se ve, y el mapa parecía vacío. Pasa cuando la
  // ventana no se ensancha (200 m justos) y cae entre dos vértices de la malla
  // de 100 m — 82+850 → 82+1050 solo contiene el 82+900. Se pinta ese punto:
  // es lo único que se sabe, y es verdad.
  if (vertices.length === 1) {
    return { center, point: vertices[0], path: [] };
  }

  return { center, point: null, path: vertices };
}

/**
 * El vértice de en medio del trazado. El índice es el de siempre
 * (`round(n / 2) - 1`): cambiarlo movería el encuadre de todos los mapas que ya
 * se pintan bien.
 */
function centerOf(vertices: MeshPoint[]): MeshPoint | null {
  if (vertices.length === 0) return null;
  return vertices[Math.max(0, Math.round(vertices.length / 2) - 1)];
}

export default locationMapGeometry;
